<script setup lang="ts">
import { computed, provide, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import api from '@/api';
import { trackingSurveyEvent } from '@/api/tracking';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import { isAxiosError } from 'axios';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import AddShareSurveyUserModal from '@/components/share-survey/add-share-survey-user-modal.vue';
import EditTitleModal from '@/components/share-survey/edit-title-modal.vue';
import ShareSurveyHeader from '@/components/share-survey/share-survey-header.vue';
import SurveyInput from '@/components/survey/input/survey-input.vue';
import NotAllowed from '@/components/survey/not-allowed.vue';
import SurveyHistory from '@/components/survey/survey-history.vue';
import SurveyRelatedQuestions from '@/components/survey/survey-related-questions.vue';
import VisasqNowQuestionDraft from '@/components/survey/visasq/visasq-now-question-draft.vue';
import {
  ShareSurvey,
  ShareSurveyMode,
  ShareSurveyUser,
  SpecifiedMethod,
  SurveyInputValue,
} from '@/types';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { formatDate } from '@/utils/formatters';
import { specifiedMethodKey } from '@/utils/injectionKeys';
import { SHARE_SURVEY_PAGE_NAME } from '@/utils/survey/constants';
import { trackSurveyPageView } from '@/utils/survey/tracking';
import { formatDateForTracking } from '@/utils/trackingHelper';
import { useEmitter } from '@/utils/vue';
import { useStore } from '@/utils/vue';

const props = defineProps<{ uuid: string }>();
const shareSurvey = ref<ShareSurvey>();

type PageState = 'loading' | 'invalidUrl' | 'forbidden' | 'loaded';
const pageState = ref<PageState>('loading');

const store = useStore();
const router = useRouter();
const emitter = useEmitter();
const { createSnackbar } = useSnackbar();
const { getCurrentPathForTracking } = useCurrentPathForTracking();
const userInfo = computed(() => store.state.userInfo.userInfo);
const isOwner = computed(() => {
  if (!shareSurvey.value) return false;
  return shareSurvey.value.owner?.id === userInfo.value?.id;
});
const specifiedMethod = ref<SpecifiedMethod>();
provide(specifiedMethodKey, specifiedMethod);

const mode = computed<ShareSurveyMode>(() => {
  if (isOwner.value && shareSurvey.value?.is_shared) {
    return 'edit';
  } else if (isOwner.value && !shareSurvey.value?.is_shared) {
    return 'create';
  }
  return 'view';
});

const expiredAt = computed(() =>
  shareSurvey.value?.expired_at
    ? new Date(shareSurvey.value.expired_at)
    : undefined,
);

const isShareSurveyUserModalOpen = ref(false);
const isEditTitleModalOpen = ref(false);

const fetchShareSurvey = async (uuid: string) => {
  pageState.value = 'loading';
  if (!uuid) {
    pageState.value = 'invalidUrl';
    shareSurvey.value = undefined;
    // eslint-disable-next-line no-console
    console.error('uuid is required');
    return;
  }
  try {
    shareSurvey.value = await api.fetchShareSurvey(uuid);
    pageState.value = 'loaded';
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 403) {
      pageState.value = 'forbidden';
    } else {
      pageState.value = 'invalidUrl';
    }
    shareSurvey.value = undefined;
    throw error;
  }
};

const relatedQuestions = computed(() => {
  const blocks = shareSurvey.value?.share_survey_blocks;
  return blocks?.at(-1)?.related_questions ?? [];
});

const updateShareSurvey = async (
  shareSurveyUsers: ShareSurveyUser[],
  isAllUsersAccessible: boolean,
  expiredAt: Date,
) => {
  const userIds = shareSurveyUsers
    .map(user => user.id)
    .filter(userId => userId !== undefined);

  try {
    shareSurvey.value = await api.putShareSurvey(props.uuid, {
      shareSurveyUserIds: userIds,
      isAllUsersAccessible,
      expiredAt,
    });
    trackingSurveyEvent('share_survey_share', {
      page: {
        name: SHARE_SURVEY_PAGE_NAME,
        url: getCurrentPathForTracking(),
      },
      session_id: props.uuid,
      share_expiration: formatDateForTracking(expiredAt),
      shared_user_ids: isAllUsersAccessible ? 'all' : userIds,
    });
  } catch (error) {
    createSnackbar({
      message: 'シェアスレッドの共有に失敗しました',
      type: 'error',
    });
    throw error;
  }

  createSnackbar({
    message: 'シェアスレッドの共有設定を更新しました',
    type: 'success',
  });
};

// 初期化
watch(
  () => props.uuid,
  uuid => {
    fetchShareSurvey(uuid);
    trackSurveyPageView(
      SHARE_SURVEY_PAGE_NAME,
      getCurrentPathForTracking(),
      undefined,
      uuid,
    );
  },
  { immediate: true },
);

const startSurvey = async ({
  question,
  filterInfo,
  deepMode,
}: SurveyInputValue) => {
  const surveySession = await api.createSurveySession(
    question,
    shareSurvey.value?.uuid,
  );
  const surveySessionId = surveySession.session_id;
  await store.dispatch('survey/setSessionAllState', {
    sessionId: surveySessionId,
    question,
    filterInfo,
    deepMode,
    specifiedMethod: specifiedMethod.value,
    executionFrom: 'share_surveys',
    shareSurveysKind: shareSurvey.value?.is_sample ? 'sample' : 'team_shared',
    surveyType: 'share_survey',
    shareSurveyLastRequestId:
      shareSurvey.value?.share_survey_blocks?.at(-1)?.id,
    shareSurveyPromptTypeName: 'custom_input',
  });

  router.push({
    name: 'surveySession',
    params: { survey_session_id: surveySessionId },
  });
};

const startSurveyFromRelatedQuestion = async (question: string) => {
  const surveySession = await api.createSurveySession(
    question,
    shareSurvey.value?.uuid,
  );
  const surveySessionId = surveySession.session_id;
  await store.dispatch('survey/setSessionAllState', {
    sessionId: surveySessionId,
    question,
    filterInfo: {
      searchScope: 'all',
      docTypes: [],
      specifiedFiles: [],
    },
    deepMode: false,
    specifiedMethod: undefined,
    executionFrom: 'share_surveys',
    shareSurveysKind: shareSurvey.value?.is_sample ? 'sample' : 'team_shared',
    surveyType: 'share_survey',
    shareSurveyLastRequestId:
      shareSurvey.value?.share_survey_blocks?.at(-1)?.id,
    shareSurveyPromptTypeName: 'related_question',
  });

  router.push({
    name: 'surveySession',
    params: { survey_session_id: surveySessionId },
  });
};

const deleteHandler = () => {
  const payload = {
    headerText: 'シェアスレッドの削除',
    bodyText: `「${shareSurvey.value?.title}」を削除しますか？`,
    action: deleteShareSurvey,
    btnText: '削除',
    cancelBtnText: 'キャンセル',
    isBtnDisabled: false,
  };
  store.commit('confirmationModal/setTextAndAction', payload);
  store.commit('confirmationModal/showConfirmationModal');
};

const deleteShareSurvey = async () => {
  try {
    await api.deleteShareSurvey(props.uuid);
    trackingSurveyEvent('share_survey_delete', {
      page: {
        name: SHARE_SURVEY_PAGE_NAME,
        url: getCurrentPathForTracking(),
      },
      session_id: props.uuid,
    });
    createSnackbar({
      message: 'シェアスレッドを削除しました',
      type: 'success',
    });
    emitter.emit('update-share-survey');
    router.push({ name: 'surveyTop' });
  } catch (_error) {
    createSnackbar({
      message: 'シェアスレッドの削除に失敗しました',
      type: 'error',
    });
  }
};

const editShareSurveyTitle = async (newTitle: string) => {
  try {
    shareSurvey.value = await api.putShareSurvey(props.uuid, {
      title: newTitle,
    });
  } catch (error) {
    createSnackbar({
      message: 'シェアスレッドのタイトル編集に失敗しました',
      type: 'error',
    });
    throw error;
  }

  emitter.emit('update-share-survey');
  createSnackbar({
    message: 'シェアスレッドのタイトルを編集しました',
    type: 'success',
  });
};
</script>

<template>
  <div class="survey-session-container">
    <template v-if="pageState === 'forbidden'">
      <div class="survey-not-allowed">
        <NotAllowed />
      </div>
    </template>
    <template v-else-if="pageState === 'loaded' && shareSurvey">
      <div
        class="c-text-regular--s not-shared-message"
        v-if="!shareSurvey.is_shared"
      >
        <div class="empty-message-content">
          <DgrIcon name="lock-fill" size="small" />
          <div>このシェアスレッドはまだ誰とも共有されていません</div>
        </div>
      </div>
      <ShareSurveyHeader
        v-if="shareSurvey"
        :title="shareSurvey.title"
        :use-anews-icon="!shareSurvey.owner.image_url"
        :user-name="shareSurvey.owner.user_name"
        :user-icon-url="shareSurvey.owner.image_url"
        :is-owner="isOwner"
        :mode="mode"
        :updated-at="formatDate(shareSurvey.updated_at)"
        @share="isShareSurveyUserModalOpen = true"
        @delete="deleteHandler"
        @edit-title="isEditTitleModalOpen = true"
      />
      <div class="content-wrapper">
        <div
          class="content"
          v-for="block in shareSurvey?.share_survey_blocks"
          :key="block.id"
        >
          <SurveyHistory
            :session-id="shareSurvey?.uuid"
            :request-id="String(block.id)"
            :question="block.question"
            :incomplete="false"
            :sources="block.sources"
            :deep-mode-processes="[]"
            origin-markdown-reasoning=""
            :reasoning="[]"
            :origin-markdown-summary="block.model_response || ''"
            :summary="[]"
            :enable-animation="false"
            :enable-feedback="false"
            :enable-source-tab="false"
            :feedback-type="undefined"
            :deep-mode="false"
            :status="{
              isInProgress: false,
              isNoHit: false,
              isError: false,
              progressMessage: '',
            }"
            :filter-info="{
              searchScope: 'all',
              docTypes: [],
              specifiedFiles: [],
            }"
            :page-name="SHARE_SURVEY_PAGE_NAME"
          ></SurveyHistory>
          <VisasqNowQuestionDraft
            v-for="(
              visasqNowQuestionDraft, idx
            ) in block.share_survey_visasq_now_question_draft_blocks"
            :key="`${block.id}-${idx}`"
            :enable-animation="false"
            :is-generating="false"
            :is-error="false"
            :survey-history-question="block.question"
            :title="visasqNowQuestionDraft.title"
            :background="visasqNowQuestionDraft.background"
            :question="visasqNowQuestionDraft.question"
            :sample-answer="visasqNowQuestionDraft.sample_answer"
            :id="visasqNowQuestionDraft.id"
            :session-id="shareSurvey?.uuid"
          />
        </div>
        <SurveyRelatedQuestions
          v-if="relatedQuestions.length > 0"
          :related-questions="relatedQuestions"
          :enable-visasq-now-button="false"
          @select-question="startSurveyFromRelatedQuestion"
        />
        <div class="input-wrapper">
          <SurveyInput
            enable-transform-input
            :page-name="SHARE_SURVEY_PAGE_NAME"
            @submit="startSurvey"
          />
        </div>
      </div>
    </template>
    <template v-else-if="pageState === 'invalidUrl'">
      <div class="invalid-url-message">無効なURLです</div>
    </template>
  </div>
  <AddShareSurveyUserModal
    v-if="shareSurvey?.owner"
    :key="shareSurvey.uuid"
    :uuid="shareSurvey.uuid"
    :owner="shareSurvey.owner"
    :current-user-id="userInfo?.id"
    :mode="mode"
    :is-sample="shareSurvey.is_sample"
    :init-expired-at="expiredAt"
    :init-share-survey-users="shareSurvey.share_survey_users || []"
    :init-is-all-users-accessible="shareSurvey.is_all_users_accessible"
    v-model:open="isShareSurveyUserModalOpen"
    @update-share-survey="updateShareSurvey"
  />
  <EditTitleModal
    v-if="shareSurvey?.owner"
    :title="shareSurvey.title"
    v-model:open="isEditTitleModalOpen"
    @edit-share-survey-title="editShareSurveyTitle"
  />
</template>

<style lang="scss" scoped>
.survey-session-container {
  width: 100%;
  padding-bottom: 80px;

  .content-wrapper {
    max-width: var(--survey-session-width);
    margin: 0 auto;
    margin-bottom: 72px; // 入力欄 (survey-input.vue) にフォーカスが当たっている時に、下の方の文字が見えないので余白をとる
    padding: 0 32px;
  }

  .content {
    margin: 40px 0;
  }
}
.not-shared-message {
  padding: 8px 24px;
  display: flex;
  justify-content: center;
  .empty-message-content {
    max-width: var(--survey-session-width);
    width: 100%;
    display: flex;
    gap: 4px;
  }
}
.survey-not-allowed {
  max-width: var(--survey-session-width);
  margin: 0 auto;
  padding: 32px 24px;
}
.invalid-url-message {
  padding: 32px 24px;
}
</style>
